import { useEffect, useRef } from "react";
import { useEmailBuilderStore } from "@/store/emailBuilderStore";

interface UseAutoSaveOptions {
  interval?: number; // milliseconds, default 30000 (30s)
  enabled?: boolean; // default true
  checkpointInterval?: number; // How many auto-saves before creating a checkpoint, default 5
}

/**
 * Auto-save hook for email builder
 * 
 * Automatically saves the document when it's dirty at the specified interval.
 * Creates version checkpoints after a certain number of auto-saves.
 * Ensures save on unmount to prevent data loss.
 * 
 * @param options Configuration options for auto-save behavior
 * @returns void
 * 
 * @example
 * ```tsx
 * function EmailBuilderPage() {
 *   useAutoSave({ interval: 30000, checkpointInterval: 5 });
 *   // ... rest of component
 * }
 * ```
 */
export function useAutoSave(options: UseAutoSaveOptions = {}) {
  const { 
    interval = 30000, // 30 seconds default
    enabled = true, 
    checkpointInterval = 5 
  } = options;

  const lastSaveRef = useRef<Date | null>(null);
  const saveCountRef = useRef<number>(0);
  const isUnmountingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) return;

    // Auto-save interval
    const autoSaveTimer = setInterval(async () => {
      const state = useEmailBuilderStore.getState();

      // Only auto-save if:
      // 1. Document is loaded
      // 2. Document is dirty (has unsaved changes)
      // 3. Not currently saving
      // 4. Not unmounting (to avoid race conditions)
      if (
        state.document && 
        state.isDirty && 
        !state.isSaving && 
        !isUnmountingRef.current
      ) {
        try {
          await state.saveDocument();
          lastSaveRef.current = new Date();
          saveCountRef.current += 1;

          // Create checkpoint every N auto-saves
          if (saveCountRef.current % checkpointInterval === 0) {
            await state.createCheckpoint(
              `Auto-checkpoint after ${saveCountRef.current} saves`,
              "AutoSave"
            );
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : "Unknown error";
          console.error("Auto-save failed:", errorMsg, error);
          // Don't throw - auto-save should be silent and retry on next interval
          // Error is already set in the store by saveDocument
        }
      }
    }, interval);

    return () => {
      clearInterval(autoSaveTimer);
    };
  }, [enabled, interval, checkpointInterval]);

  // Save on unmount if dirty
  useEffect(() => {
    // Capture document ID at mount time to verify it hasn't changed on unmount
    const documentId = useEmailBuilderStore.getState().document?.id;
    
    return () => {
      isUnmountingRef.current = true;
      const state = useEmailBuilderStore.getState();
      
      // Fire-and-forget save on unmount
      // This is best-effort - can't await in cleanup
      // Only save if SAME document is still loaded (prevent race condition)
      if (
        state.document &&
        state.document.id === documentId && // Verify document hasn't changed
        state.isDirty &&
        !state.isSaving
      ) {
        state.saveDocument().catch((error) => {
          console.error("Save on unmount failed:", error);
        });
      }
    };
  }, []); // Empty deps - captures documentId at mount, safe for cleanup

  // Expose auto-save status via store subscription (optional)
  // This allows components to show auto-save indicator
  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = useEmailBuilderStore.subscribe(
      (state) => {
        if (state.lastSaved) {
          lastSaveRef.current = state.lastSaved;
        }
      }
    );

    return unsubscribe;
  }, [enabled]);
}

/**
 * Hook to get auto-save status information
 * Useful for displaying auto-save indicators in the UI
 * 
 * @returns Object with save status information
 * 
 * @example
 * ```tsx
 * function SaveIndicator() {
 *   const { isSaving, lastSaved, isDirty } = useAutoSaveStatus();
 *   
 *   if (isSaving) return <span>Saving...</span>;
 *   if (isDirty) return <span>Unsaved changes</span>;
 *   if (lastSaved) return <span>Saved {formatTime(lastSaved)}</span>;
 *   return null;
 * }
 * ```
 */
export function useAutoSaveStatus() {
  const isSaving = useEmailBuilderStore((state) => state.isSaving);
  const lastSaved = useEmailBuilderStore((state) => state.lastSaved);
  const isDirty = useEmailBuilderStore((state) => state.isDirty);
  const saveError = useEmailBuilderStore((state) => state.saveError);

  return {
    isSaving,
    lastSaved,
    isDirty,
    saveError,
    hasUnsavedChanges: isDirty,
  };
}
